import { CSSResultGroup, TemplateResult, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state } from 'lit/decorators.js';
import { SlotsNGController as SlotsController } from '../../controllers/slots-ng-controller';
import { OutlineElement } from '../../base/outline-element/outline-element';
import '../sic-icon/sic-icon';
import componentStyles from './sic-card.css.lit';

type AllowedIconTypes = 'custom' | 'fa';

/**
 * The SIC Card component
 * @element sic-card
 *
 * @slot media
 * @slot icon
 * @slot content
 *
 */
@customElement('sic-card')
export class SicCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotsController(this);

  /**
   * Icon
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'icon',
  })
  icon: string;

  /**
   * IconType
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'icon-type',
  })
  iconType: AllowedIconTypes = 'custom';

  @state()
  private hasImage: boolean;

  @state()
  private hasIcon: boolean;

  mediaClasses() {
    const imageEmpty = !this.slots.slotHasContent('image');

    this.hasImage = this.slots.slotExists('image') !== false;
    this.hasIcon = this.slots.slotExists('icon') !== false;

    return {
      'teaser-card': true,
      'card-wrapper--with-image': this.hasImage,
      'image-empty': this.hasImage && imageEmpty,
      'card-wrapper--with-icon': !this.hasImage && this.hasIcon,
      'card-wrapper--no-media': !this.hasImage && !this.hasIcon,
    };
  }

  render(): TemplateResult {
    const wrapperClasses = this.mediaClasses();

    return html`
      <section class="card">
        <div class="card-wrapper ${classMap(wrapperClasses)}">
          ${this.mediaTemplate()}
          ${this.slots.conditionalSlot('content', false, 'card-content')}
        </div>
      </section>
    `;
  }
  mediaTemplate(): TemplateResult {
    if (!this.hasImage && this.hasIcon) {
      return html`
        ${this.slots.conditionalSlot(
          'icon',
          true,
          'media card-media card-icon'
        )}
      `;
    } else {
      return html`
        ${this.slots.conditionalSlot(
          'image',
          true,
          'media card-media card-image'
        )}
      `;
    }
  }

  // TODO: remove if we want use <sic-heading>.
  headingTemplate(): TemplateResult {
    if (this.slots.slotExists('heading')) {
      return html` <div class="card-heading">
        <sic-heading level-size="1">
          <slot name="heading"></slot>
        </sic-heading>
      </div>`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-card': SicCard;
  }
}
